import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Card = {
  uuid: string;
  brand: string | null;
  card_type: string | null;
  pan_last4: string;
  pan: string | null;
  expiry: string | null;
  cardholder: string | null;
  account_uuid: string | null;
  business_name: string | null;
  start_date: string | null;
  status: string;
  notes: string | null;
  ledger_seq: number;
};

type Acct = {
  uuid: string;
  institution: string;
  account_type: string | null;
  account_holder_name: string | null;
  issuing_branch: string | null;
  account_class: string | null;
  opened_date: string | null;
  closed_date: string | null;
  ownership: string | null;
  last4: string | null;
  account_number: string | null;
  sort_code: string | null;
  status: string;
  notes: string | null;
  ledger_seq: number;
};

async function jget(u: string) { return fetch(u, { cache: 'no-store' }).then((r) => r.json()); }
async function jpost(u: string, body: unknown) {
  const r = await fetch(u, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: r.ok, body: await r.json() };
}
async function jpatch(u: string, body: unknown) {
  const r = await fetch(u, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: r.ok, body: await r.json() };
}
async function jdel(u: string) {
  const r = await fetch(u, { method: 'DELETE' });
  return { ok: r.ok, body: await r.json() };
}

type EditAcctForm = {
  institution: string; account_type: string; account_number: string; sort_code: string;
  account_holder_name: string; issuing_branch: string; account_class: string;
  opened_date: string; closed_date: string; status: string; notes: string;
};
type EditCardForm = {
  brand: string; card_type: string; pan: string; expiry: string; cardholder: string;
  business_name: string; start_date: string; status: string; notes: string; account_uuid: string;
};

function blankEditAcct(a: Acct): EditAcctForm {
  return {
    institution: a.institution ?? '',
    account_type: a.account_type ?? 'bank',
    account_number: a.account_number ?? '',
    sort_code: a.sort_code ?? '',
    account_holder_name: a.account_holder_name ?? '',
    issuing_branch: a.issuing_branch ?? '',
    account_class: a.account_class ?? 'current',
    opened_date: a.opened_date ?? '',
    closed_date: a.closed_date ?? '',
    status: a.status ?? 'open',
    notes: a.notes ?? '',
  };
}
function blankEditCard(c: Card): EditCardForm {
  return {
    brand: c.brand ?? '',
    card_type: c.card_type ?? 'debit',
    pan: c.pan ?? '',
    expiry: c.expiry ?? '',
    cardholder: c.cardholder ?? '',
    business_name: c.business_name ?? '',
    start_date: c.start_date ?? '',
    status: c.status ?? 'active',
    notes: c.notes ?? '',
    account_uuid: c.account_uuid ?? '',
  };
}

export function AccountsCards() {
  const [cards, setCards] = useState<Card[]>([]);
  const [accts, setAccts] = useState<Acct[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // add-form toggles
  const [showAddAcct, setShowAddAcct] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);

  // inline edit state
  const [editingAcct, setEditingAcct] = useState<string | null>(null);
  const [editAcctForm, setEditAcctForm] = useState<EditAcctForm | null>(null);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [editCardForm, setEditCardForm] = useState<EditCardForm | null>(null);
  const [replacingCard, setReplacingCard] = useState<string | null>(null);
  const [replaceForm, setReplaceForm] = useState<{ pan: string; expiry: string; start_date: string; brand: string; card_type: string; business_name: string } | null>(null);

  // add-account form fields
  const [aInst, setAInst] = useState('');
  const [aAccountType, setAAccountType] = useState('bank');
  const [aNum, setANum] = useState('');
  const [aSort, setASort] = useState('');
  const [aHolderName, setAHolderName] = useState('');
  const [aBranch, setABranch] = useState('');
  const [aClass, setAClass] = useState('current');
  const [aOpenedDate, setAOpenedDate] = useState('');
  const [aNotes, setANotes] = useState('');

  // add-card form fields
  const [cBrand, setCBrand] = useState('');
  const [cType, setCType] = useState('debit');
  const [cPan, setCPan] = useState('');
  const [cExp, setCExp] = useState('');
  const [cCardholder, setCCardholder] = useState('');
  const [cAccountUuid, setCAccountUuid] = useState('');
  const [cStartDate, setCStartDate] = useState('');
  const [cBusinessName, setCBusinessName] = useState('');
  const [cNotes, setCNotes] = useState('');

  const load = () => {
    jget('/api/cards').then((d) => setCards(d.cards ?? []));
    jget('/api/accounts').then((d) => setAccts(d.accounts ?? []));
  };
  useEffect(load, []);

  // Group cards by account_uuid
  const cardsByAcct: Record<string, Card[]> = {};
  const unlinkedCards: Card[] = [];
  for (const c of cards) {
    if (c.account_uuid) { (cardsByAcct[c.account_uuid] ??= []).push(c); }
    else { unlinkedCards.push(c); }
  }

  async function addAcct(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!aInst.trim()) return setErr('Institution required');
    const { ok, body } = await jpost('/api/accounts', {
      institution: aInst, account_type: aAccountType, account_number: aNum || undefined,
      sort_code: aSort || undefined, account_holder_name: aHolderName || null,
      issuing_branch: aBranch || null, account_class: aClass || null,
      opened_date: aOpenedDate || null, notes: aNotes || null,
    });
    if (!ok) return setErr(body.error ?? 'Failed');
    setAInst(''); setANum(''); setASort(''); setAHolderName('');
    setABranch(''); setAOpenedDate(''); setANotes('');
    setShowAddAcct(false);
    load();
  }

  async function saveAcct(uuid: string) {
    if (!editAcctForm) return;
    setErr(null);
    const { ok, body } = await jpatch(`/api/accounts/${uuid}`, editAcctForm);
    if (!ok) return setErr(body.error ?? 'Failed to update account');
    setEditingAcct(null); setEditAcctForm(null);
    load();
  }

  async function removeAcct(uuid: string, institution: string) {
    if (!window.confirm(`Remove account "${institution}"? This will crypto-shred the account number and mark it removed.`)) return;
    setErr(null);
    const { ok, body } = await jdel(`/api/accounts/${uuid}`);
    if (!ok) return setErr(body.error ?? 'Failed to remove account');
    load();
  }

  async function addCard(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!cPan.trim()) return setErr('Card number required');
    const { ok, body } = await jpost('/api/cards', {
      brand: cBrand || null, card_type: cType, pan: cPan, expiry: cExp || null,
      cardholder: cCardholder || null, account_uuid: cAccountUuid || null,
      start_date: cStartDate || null, business_name: cBusinessName || null, notes: cNotes || null,
    });
    if (!ok) return setErr(body.error ?? 'Failed');
    setCBrand(''); setCPan(''); setCExp(''); setCCardholder('');
    setCAccountUuid(''); setCStartDate(''); setCBusinessName(''); setCNotes('');
    setShowAddCard(false);
    load();
  }

  async function saveCard(uuid: string) {
    if (!editCardForm) return;
    setErr(null);
    const { ok, body } = await jpatch(`/api/cards/${uuid}`, {
      ...editCardForm,
      account_uuid: editCardForm.account_uuid || null,
    });
    if (!ok) return setErr(body.error ?? 'Failed to update card');
    setEditingCard(null); setEditCardForm(null);
    load();
  }

  async function removeCard(uuid: string, label: string) {
    if (!window.confirm(`Remove card "${label}"? This will crypto-shred the card number.`)) return;
    setErr(null);
    const { ok, body } = await jdel(`/api/cards/${uuid}`);
    if (!ok) return setErr(body.error ?? 'Failed to remove card');
    load();
  }

  async function replaceCard(uuid: string) {
    if (!replaceForm) return;
    setErr(null);
    const { ok, body } = await jpost(`/api/cards/${uuid}/replace`, {
      pan: replaceForm.pan, expiry: replaceForm.expiry || null,
      start_date: replaceForm.start_date || null, brand: replaceForm.brand || null,
      card_type: replaceForm.card_type || null, business_name: replaceForm.business_name || null,
    });
    if (!ok) return setErr(body.error ?? 'Failed to replace card');
    setReplacingCard(null); setReplaceForm(null);
    load();
  }

  function cardDisplayStr(c: Card) {
    if (c.pan) return c.pan;
    if (c.pan_last4) return `•••• •••• •••• ${c.pan_last4}`;
    return '—';
  }

  function renderCardBlock(c: Card) {
    const isEditing = editingCard === c.uuid;
    const label = `${c.brand ?? 'Card'} ••${c.pan_last4}`;
    const isReplacing = replacingCard === c.uuid;
    if (isReplacing && replaceForm) {
      const f = replaceForm;
      const set = (k: keyof typeof replaceForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setReplaceForm({ ...f, [k]: e.target.value });
      return (
        <div key={c.uuid} style={{ background: 'var(--surface2)', borderRadius: 6, padding: 12, marginTop: 8 }}>
          <div className="panel-head"><h4 style={{ margin: 0 }}>Replace {c.brand ?? 'card'} ••{c.pan_last4}</h4></div>
          <p className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>The old card will be marked replaced. New card inherits the account link.</p>
          <label className="fld"><span>New card number</span>
            <input value={f.pan} onChange={set('pan')} autoComplete="off" placeholder="•••• •••• •••• ••••" />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Brand</span><input value={f.brand} onChange={set('brand')} /></label>
            <label className="fld" style={{ width: 110 }}><span>Type</span>
              <select value={f.card_type} onChange={set('card_type')}>
                <option value="debit">debit</option><option value="credit">credit</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Expiry</span><input value={f.expiry} onChange={set('expiry')} placeholder="MM/YY" /></label>
            <label className="fld" style={{ flex: 1 }}><span>Start date</span><input type="date" value={f.start_date} onChange={set('start_date')} /></label>
            <label className="fld" style={{ flex: 2 }}><span>Business name</span><input value={f.business_name} onChange={set('business_name')} /></label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary" onClick={() => replaceCard(c.uuid)}>Replace card</button>
            <button className="btn btn-ghost" onClick={() => { setReplacingCard(null); setReplaceForm(null); }}>Cancel</button>
          </div>
        </div>
      );
    }
    if (isEditing && editCardForm) {
      const f = editCardForm;
      const set = (k: keyof EditCardForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setEditCardForm({ ...f, [k]: e.target.value });
      return (
        <div key={c.uuid} style={{ background: 'var(--surface2)', borderRadius: 6, padding: 12, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Brand</span><input value={f.brand} onChange={set('brand')} /></label>
            <label className="fld" style={{ width: 110 }}><span>Type</span>
              <select value={f.card_type} onChange={set('card_type')}>
                <option value="debit">debit</option><option value="credit">credit</option>
              </select>
            </label>
          </div>
          <label className="fld"><span>Card number (leave blank to keep existing)</span>
            <input value={f.pan} onChange={set('pan')} autoComplete="off" placeholder="Enter new number or leave blank" />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Expiry</span><input value={f.expiry} onChange={set('expiry')} placeholder="MM/YY" /></label>
            <label className="fld" style={{ flex: 2 }}><span>Cardholder</span><input value={f.cardholder} onChange={set('cardholder')} /></label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Business name</span><input value={f.business_name} onChange={set('business_name')} /></label>
            <label className="fld" style={{ flex: 1 }}><span>Start date</span><input type="date" value={f.start_date} onChange={set('start_date')} /></label>
          </div>
          <label className="fld"><span>Linked account</span>
            <select value={f.account_uuid} onChange={set('account_uuid')}>
              <option value="">— none —</option>
              {accts.map((a) => <option key={a.uuid} value={a.uuid}>{a.institution}{a.account_number ? ` (${a.account_number})` : ''}</option>)}
            </select>
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Status</span>
              <select value={f.status} onChange={set('status')}>
                <option value="active">active</option><option value="expired">expired</option>
                <option value="cancelled">cancelled</option><option value="lost">lost</option>
              </select>
            </label>
          </div>
          <label className="fld"><span>Notes</span><textarea value={f.notes} onChange={set('notes')} rows={2} /></label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary" onClick={() => saveCard(c.uuid)}>Save</button>
            <button className="btn btn-ghost" onClick={() => { setEditingCard(null); setEditCardForm(null); }}>Cancel</button>
          </div>
        </div>
      );
    }
    return (
      <div key={c.uuid} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span className="badge badge-mute">{(c.brand ?? 'CARD').toUpperCase()}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ledger-text mono" style={{ wordBreak: 'break-word' }}>{cardDisplayStr(c)}</div>
            <div className="ledger-time">
              {c.card_type}{c.cardholder ? ` · ${c.cardholder}` : ''}
              {c.business_name ? ` · ${c.business_name}` : ''}
              {c.expiry ? ` · exp ${c.expiry}` : ''}
              {c.start_date ? ` · from ${c.start_date}` : ''} · {c.status}
            </div>
            {c.notes && <div className="muted3" style={{ fontSize: 12, marginTop: 2 }}>{c.notes}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <Attachments entityType="bank_card" entityUuid={c.uuid} compact />
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setEditingCard(c.uuid); setEditCardForm(blankEditCard(c)); }}>Edit</button>
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => {
              setReplacingCard(c.uuid);
              setReplaceForm({ pan: '', expiry: '', start_date: '', brand: c.brand ?? '', card_type: c.card_type ?? 'debit', business_name: c.business_name ?? '' });
            }}>Replace</button>
            <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--bad)' }} onClick={() => removeCard(c.uuid, label)}>Remove</button>
          </div>
        </div>
      </div>
    );
  }

  function renderAcctBlock(a: Acct) {
    const isEditing = editingAcct === a.uuid;
    const linked = cardsByAcct[a.uuid] ?? [];
    if (isEditing && editAcctForm) {
      const f = editAcctForm;
      const set = (k: keyof EditAcctForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setEditAcctForm({ ...f, [k]: e.target.value });
      return (
        <div className="card card-pad" key={a.uuid} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 2 }}><span>{f.account_type === 'crypto' ? 'Exchange / platform' : 'Institution'}</span><input value={f.institution} onChange={set('institution')} /></label>
            <label className="fld" style={{ width: 150 }}><span>Account type</span>
              <select value={f.account_type} onChange={set('account_type')}>
                <option value="bank">Bank</option>
                <option value="credit_card">Credit Card</option>
                <option value="post_office">Post Office</option>
                <option value="crypto">Crypto currency</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>{f.account_type === 'crypto' ? 'Account ID / wallet reference (leave blank to keep)' : 'Account number (leave blank to keep existing)'}</span>
              <input value={f.account_number} onChange={set('account_number')} autoComplete="off" />
            </label>
            {f.account_type !== 'crypto' && (
              <label className="fld" style={{ width: 130 }}><span>Sort code</span><input value={f.sort_code} onChange={set('sort_code')} placeholder="00-00-00" /></label>
            )}
          </div>
          <label className="fld"><span>Account holder name</span><input value={f.account_holder_name} onChange={set('account_holder_name')} /></label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Issuing branch</span><input value={f.issuing_branch} onChange={set('issuing_branch')} /></label>
            <label className="fld" style={{ width: 130 }}><span>Account class</span>
              <select value={f.account_class} onChange={set('account_class')}>
                <option value="current">current</option><option value="savings">savings</option><option value="business">business</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Opened date</span><input type="date" value={f.opened_date} onChange={set('opened_date')} /></label>
            <label className="fld" style={{ flex: 1 }}><span>Closed date</span><input type="date" value={f.closed_date} onChange={set('closed_date')} /></label>
          </div>
          <label className="fld"><span>Status</span>
            <select value={f.status} onChange={set('status')}>
              <option value="open">open</option><option value="closed">closed</option><option value="disputed">disputed</option>
            </select>
          </label>
          <label className="fld"><span>Notes</span><textarea value={f.notes} onChange={set('notes')} rows={2} /></label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary" onClick={() => saveAcct(a.uuid)}>Save account</button>
            <button className="btn btn-ghost" onClick={() => { setEditingAcct(null); setEditAcctForm(null); }}>Cancel</button>
          </div>
        </div>
      );
    }
    return (
      <div className="card card-pad" key={a.uuid} style={{ marginBottom: 12 }}>
        {/* Account header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span className="badge badge-mute">{(a.account_type ?? 'ACCT').toUpperCase()}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ledger-text">
              <strong>{a.institution}</strong>
              {a.account_holder_name ? <span className="muted3"> · {a.account_holder_name}</span> : null}
              {' '}<span className="mono muted3">
                {a.account_number
                  ? (a.sort_code ? `${a.account_number}  ${a.sort_code}` : a.account_number)
                  : (a.last4 ? `••••${a.last4}` : '—')}
              </span>
            </div>
            <div className="ledger-time">
              {a.account_class ? `${a.account_class} · ` : ''}
              {a.status}
              {a.issuing_branch ? ` · ${a.issuing_branch}` : ''}
              {a.opened_date ? ` · opened ${a.opened_date}` : ''}
              {a.closed_date ? ` · closed ${a.closed_date}` : ''}
            </div>
            {a.notes && <div className="muted3" style={{ fontSize: 12, marginTop: 2 }}>{a.notes}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <Attachments entityType="bank_account" entityUuid={a.uuid} compact />
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setEditingAcct(a.uuid); setEditAcctForm(blankEditAcct(a)); }}>Edit</button>
            <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--bad)' }} onClick={() => removeAcct(a.uuid, a.institution)}>Remove</button>
          </div>
        </div>

        {/* Linked cards */}
        {linked.length > 0 && (
          <div style={{ marginTop: 10, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
            <div className="muted3" style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>LINKED CARDS</div>
            {linked.map(renderCardBlock)}
          </div>
        )}
      </div>
    );
  }

  return (
    <Page eyebrow="Records" title="Accounts & cards" actions={<span className="pill pill-ok"><span className="dot dot-ok" />encrypted at rest</span>}>
      {err && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {/* Account blocks */}
      {accts.length > 0
        ? accts.map(renderAcctBlock)
        : <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No accounts yet.</div>}

      {/* Unlinked cards */}
      {unlinkedCards.length > 0 && (
        <div className="card card-pad" style={{ marginBottom: 12 }}>
          <div className="panel-head"><h3>Cards not linked to an account</h3></div>
          {unlinkedCards.map(renderCardBlock)}
        </div>
      )}
      {cards.length === 0 && accts.length === 0 && (
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No cards yet either.</div>
      )}

      {/* Add account toggle */}
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-ghost" onClick={() => setShowAddAcct((v) => !v)}>
          {showAddAcct ? '− Hide add account' : '+ Add account'}
        </button>
      </div>
      {showAddAcct && (
        <div className="card card-pad" style={{ marginBottom: 12 }}>
          <div className="panel-head"><h3>Add account</h3><span className="hint">encrypted at rest</span></div>
          <form onSubmit={addAcct}>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>{aAccountType === 'crypto' ? 'Exchange / platform' : 'Institution'}</span><input value={aInst} onChange={(e) => setAInst(e.target.value)} placeholder={aAccountType === 'crypto' ? 'e.g. Coinbase' : 'e.g. Lloyds'} /></label>
              <label className="fld" style={{ width: 150 }}><span>Account type</span>
                <select value={aAccountType} onChange={(e) => setAAccountType(e.target.value)}>
                  <option value="bank">Bank</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="post_office">Post Office</option>
                  <option value="crypto">Crypto currency</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>{aAccountType === 'crypto' ? 'Account ID / wallet reference' : 'Account number'}</span><input value={aNum} onChange={(e) => setANum(e.target.value)} autoComplete="off" /></label>
              {aAccountType !== 'crypto' && (
                <label className="fld" style={{ width: 110 }}><span>Sort code</span><input value={aSort} onChange={(e) => setASort(e.target.value)} placeholder="00-00-00" /></label>
              )}
            </div>
            <label className="fld"><span>Account holder name</span><input value={aHolderName} onChange={(e) => setAHolderName(e.target.value)} placeholder="Name on account" /></label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Issuing branch</span><input value={aBranch} onChange={(e) => setABranch(e.target.value)} /></label>
              <label className="fld" style={{ width: 130 }}><span>Account class</span>
                <select value={aClass} onChange={(e) => setAClass(e.target.value)}>
                  <option value="current">current</option><option value="savings">savings</option><option value="business">business</option>
                </select>
              </label>
            </div>
            <label className="fld"><span>Opened date</span><input type="date" value={aOpenedDate} onChange={(e) => setAOpenedDate(e.target.value)} /></label>
            <label className="fld"><span>Notes</span><textarea value={aNotes} onChange={(e) => setANotes(e.target.value)} rows={2} /></label>
            <button className="btn btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>Add account (encrypted)</button>
          </form>
        </div>
      )}

      {/* Add card toggle */}
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-ghost" onClick={() => setShowAddCard((v) => !v)}>
          {showAddCard ? '− Hide add card' : '+ Add card'}
        </button>
      </div>
      {showAddCard && (
        <div className="card card-pad" style={{ marginBottom: 12 }}>
          <div className="panel-head"><h3>Add card</h3><span className="hint">full number encrypted</span></div>
          <form onSubmit={addCard}>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Brand</span><input value={cBrand} onChange={(e) => setCBrand(e.target.value)} placeholder="Visa / Mastercard" /></label>
              <label className="fld" style={{ width: 120 }}><span>Type</span>
                <select value={cType} onChange={(e) => setCType(e.target.value)}>
                  <option value="debit">debit</option><option value="credit">credit</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Card number</span><input value={cPan} onChange={(e) => setCPan(e.target.value)} placeholder="•••• •••• •••• ••••" autoComplete="off" /></label>
              <label className="fld" style={{ width: 100 }}><span>Expiry</span><input value={cExp} onChange={(e) => setCExp(e.target.value)} placeholder="MM/YY" /></label>
            </div>
            <label className="fld"><span>Cardholder name</span><input value={cCardholder} onChange={(e) => setCCardholder(e.target.value)} placeholder="Name on card" /></label>
            <label className="fld"><span>Linked account</span>
              <select value={cAccountUuid} onChange={(e) => setCAccountUuid(e.target.value)}>
                <option value="">— none —</option>
                {accts.map((a) => <option key={a.uuid} value={a.uuid}>{a.institution}{a.account_number ? ` (${a.account_number})` : ''}</option>)}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Start date</span><input type="date" value={cStartDate} onChange={(e) => setCStartDate(e.target.value)} /></label>
              <label className="fld" style={{ flex: 1 }}><span>Business name</span><input value={cBusinessName} onChange={(e) => setCBusinessName(e.target.value)} /></label>
            </div>
            <label className="fld"><span>Notes</span><textarea value={cNotes} onChange={(e) => setCNotes(e.target.value)} rows={2} /></label>
            <button className="btn btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>Add card (encrypted)</button>
          </form>
        </div>
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
        Card numbers, account numbers and sort codes are encrypted (AES-256-GCM) at rest.
      </p>
    </Page>
  );
}
